import { isEmpty, noop } from 'lodash'
import React, { use, createContext, useState } from 'react'
import {
  BaseVisibleFieldsSettingsResponse,
  UserPreferenceNamespace,
  UserRoles,
  useVisibleFieldsSettingsConfig,
  useVisibleFieldsSettingsMutation,
  useVisibleFieldsSettingsQuery,
  VisibleFieldData
} from '~/api/userPreferences'
import { isAccessDenied } from '~/common/fetchHelpers'
import { links } from '~/common/links'
import { lazyMsg, Msg, useMsg } from '~/common/localizationUtils'
import { PageNoResultsState } from '~/components/SadStates'
import { LoadingSkeletonState } from '~/components/v2/ResultsLayout/LoadingSkeleton'
import {
  Breadcrumbs,
  Button,
  ButtonGroupActions,
  Card,
  CardActionButton,
  CardBody,
  CardHeader,
  CardHeading,
  Container,
  Divider,
  Fieldset,
  GridProps,
  Legend,
  ModalDialog,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  PageHeader,
  PageTitle,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
  useSuccessToast
} from '~/design_system'
import {
  Briefcase,
  CaretUp,
  Categories,
  Checklists,
  DateRange,
  Department,
  Docs,
  Dollars,
  Envelope,
  Experience,
  Generic,
  HireType,
  Info,
  List,
  ListCheck,
  Location,
  MoneyBag,
  Notes,
  Payment,
  Person,
  Program,
  Rolodex,
  Star,
  Target,
  Team,
  Timesheet,
  UserCards,
  Vendors,
  Warning,
  Watch,
  WorkOrders
} from '~/icons'
import ServerError from '~/job/common/ServerError'
import { CustomizeDefaultDataModal } from '~/settings/list_view_defaults/CustomizeDefaultDataModal'

export const listViewIcons: { [key: string]: any } = {
  Briefcase,
  CaretUp,
  Categories,
  Checklists,
  DateRange,
  Department,
  Docs,
  Dollars,
  Envelope,
  Experience,
  Generic,
  HireType,
  Info,
  List,
  ListCheck,
  Location,
  MoneyBag,
  Notes,
  Payment,
  Person,
  Program,
  Rolodex,
  Star,
  Target,
  Team,
  Timesheet,
  UserCards,
  Vendors,
  Warning,
  Watch,
  WorkOrders
}

export function getIcon(visibleFieldIcon: string) {
  return visibleFieldIcon in listViewIcons ? listViewIcons[visibleFieldIcon] : listViewIcons.Generic
}

export const getGrayIcon = (visibleFieldIcon: string) => {
  const Icon = getIcon(visibleFieldIcon)
  return <Icon color="gray.400" />
}

const listDataNamespaces = [
  UserPreferenceNamespace.JOB,
  UserPreferenceNamespace.JOB_APPLICATION,
  UserPreferenceNamespace.CONTRACTORS,
  UserPreferenceNamespace.WORK_ORDER
] as const

type ListDataNamespace = (typeof listDataNamespaces)[number]

const listDataTerms: Record<ListDataNamespace, React.ReactElement> = {
  [UserPreferenceNamespace.JOB]: lazyMsg('term.job', { count: 1 }),
  [UserPreferenceNamespace.JOB_APPLICATION]: lazyMsg('term.applicant', { count: 1 }),
  [UserPreferenceNamespace.CONTRACTORS]: lazyMsg('term.contractor', { count: 2 }),
  [UserPreferenceNamespace.WORK_ORDER]: lazyMsg('term.workorder', { count: 1 })
}

function useNamespaceTitle(namespace: ListDataNamespace, userRole: UserRoles) {
  const msg = useMsg()
  return msg(
    userRole === UserRoles.EMPLOYER
      ? 'settings.list_card_view_defaults.namespace_title.client_view'
      : 'settings.list_card_view_defaults.namespace_title.vendor_view',
    { list_data_type: msg(listDataTerms[namespace]) }
  )
}

export type ListCardViewFieldSettingsProps = GridProps & {
  userRole: UserRoles
  namespace: ListDataNamespace
  visibleFieldData?: BaseVisibleFieldsSettingsResponse
  visibleFields: VisibleFieldData[]
  setVisibleFields: (visibleFields: VisibleFieldData[] | []) => void
}

export type ComponentProps = {
  title?: string
  userRole?: UserRoles
}

export const ListCardViewFieldSettingsContext = createContext<ListCardViewFieldSettingsProps>(
  undefined as unknown as ListCardViewFieldSettingsProps
)

export const ListViewDefaultsApp = () => {
  const msg = useMsg()

  return (
    <>
      <PageHeader>
        <Breadcrumbs
          links={[
            links.home,
            links.companySettings,
            { name: msg('settings.list_card_view_defaults.title'), href: `/settings/list_view_defaults` }
          ]}
        />
        <PageTitle>
          <Msg id="settings.list_card_view_defaults.title" />
        </PageTitle>
      </PageHeader>
      <Container>
        <Stack spacing="lg" my="lg">
          <Text textStyle="heading3">
            <Msg id="settings.list_card_view_defaults.set_custom_data" />
          </Text>
          <Msg id="settings.list_card_view_defaults.helper_text" />
          <SimpleGrid columns={2} columnGap="lg" spacing="lg">
            {listDataNamespaces.map(namespace => (
              <ListCardViewNamespaceSettings key={namespace} namespace={namespace} />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </>
  )
}

function ListCardViewNamespaceSettings({ namespace }: { namespace: ListDataNamespace }) {
  // todo - should we make these roles namespace specific? or is it safe to assume we'll be having client & vendor settings per namespace?
  const userRoles = [UserRoles.EMPLOYER, UserRoles.VENDOR]

  return (
    <>
      {userRoles.map(role => (
        <ListCardViewFieldSettings key={`${namespace}_${role}`} userRole={role} namespace={namespace} />
      ))}
    </>
  )
}

function ListCardViewFieldSettings({ userRole, namespace }: { userRole: UserRoles; namespace: ListDataNamespace }) {
  const [visibleFields, setVisibleFields] = useState<VisibleFieldData[]>([] as VisibleFieldData[])
  const [visibleFieldData, setVisibleFieldData] = useState<BaseVisibleFieldsSettingsResponse>()
  const canUpdateConfig = useCanUpdateConfig()
  const msg = useMsg()
  const namespaceTitle = useNamespaceTitle(namespace, userRole)

  const {
    isOpen: isRestoreDefaultDataModalOpen,
    onToggle: onRestoreDefaultDataModalToggle,
    onClose: onRestoreDefaultDataModalClose
  } = useDisclosure()
  const {
    isOpen: isCustomizeDefaultDataModalOpen,
    onToggle: isCustomizeDefaultDataModalToggle,
    onClose: isCustomizeDefaultDataModalClose
  } = useDisclosure()

  const {
    isLoading,
    error: serverError,
    isError
  } = useVisibleFieldsSettingsQuery(namespace, undefined, {
    onSuccess: data => {
      const isEmployer = userRole === UserRoles.EMPLOYER
      const visibleFieldData = isEmployer ? data?.client_data : data?.vendor_data
      setVisibleFieldData(visibleFieldData)
      const fieldData = visibleFieldData?.fields
      const fieldDefaultData = visibleFieldData?.default_fields

      if (!isEmpty(fieldData)) {
        setVisibleFields(fieldData!)
      } else if (!isEmpty(fieldDefaultData)) {
        setVisibleFields(fieldDefaultData!)
      }
    }
  })

  const { mutateAsync: visibleFieldsMutate } = useVisibleFieldsSettingsMutation()

  if (isLoading) {
    return <LoadingSkeletonState />
  }

  const noResults = msg('common.ui.results', { count: 0 })
  const permissionError = msg('common.ui.permission_error')
  if (visibleFieldData == null || isAccessDenied(serverError) || !canUpdateConfig) {
    return (
      <PageNoResultsState heading={noResults} content={isAccessDenied(serverError) ? permissionError : noResults} />
    )
  }

  if (isError && serverError) return <ServerError />

  if (visibleFieldData && isEmpty(visibleFields)) {
    return null
  }

  return (
    <ListCardViewFieldSettingsContext
      value={{
        userRole,
        namespace,
        visibleFieldData,
        visibleFields,
        setVisibleFields
      }}>
      <Card>
        <CardHeader>
          <CardHeading>{namespaceTitle}</CardHeading>
          <CardActionButton onClick={onRestoreDefaultDataModalToggle}>
            <Msg id="settings.list_card_view_defaults.restore_default" />
          </CardActionButton>
        </CardHeader>
        <CardBody>
          <Msg id="settings.list_card_view_defaults.default_data.helper_text" />
          <DisplayVisibleFields
            title={msg('settings.list_card_view_defaults.current_data')}
            styleProps={{ my: 'lg' }}
          />
          <Button variant="secondary" onClick={isCustomizeDefaultDataModalToggle}>
            <Msg id="settings.list_card_view_defaults.customize_tenant_defaults" />
          </Button>
        </CardBody>
      </Card>
      <RestoreDefaultDataModal isOpen={isRestoreDefaultDataModalOpen} onClose={onRestoreDefaultDataModalClose} />
      <CustomizeDefaultDataModal
        isOpen={isCustomizeDefaultDataModalOpen}
        onClose={isCustomizeDefaultDataModalClose}
        applyAction={visibleFieldsMutate}
      />
    </ListCardViewFieldSettingsContext>
  )
}

interface RestoreDefaultDataModalProps {
  isOpen: boolean
  onClose: () => void
}

function RestoreDefaultDataModal({ isOpen, onClose }: RestoreDefaultDataModalProps) {
  const { userRole, namespace, visibleFieldData, setVisibleFields } = use(ListCardViewFieldSettingsContext)
  const msg = useMsg()
  const namespaceTitle = useNamespaceTitle(namespace, userRole)
  const successToast = useSuccessToast()

  const { mutateAsync: visibleFieldsMutate } = useVisibleFieldsSettingsMutation({
    onSuccess: () => {
      setVisibleFields(visibleFieldData?.default_fields ?? [])
      successToast(
        msg('settings.list_card_view_defaults.default_data.update_success_text', {
          data: msg('settings.list_card_view_defaults.default_data')
        })
      )
    }
  })

  return (
    <ModalDialog isOpen={isOpen} onClose={onClose}>
      <ModalDialogHeader>
        <Msg id="settings.list_card_view_defaults.restore_default.modal.title" namespace_title={namespaceTitle} />
      </ModalDialogHeader>
      <ModalDialogBody>
        <Stack spacing="lg">
          <Msg id="settings.list_card_view_defaults.restore_default.modal.helper_text" />
          <DisplayVisibleFields
            title={msg('settings.list_card_view_defaults.default_data')}
            isRestoreDefault
            styleProps={{ mt: 'lg' }}
          />
        </Stack>
      </ModalDialogBody>
      <ModalDialogFooter>
        <ButtonGroupActions>
          <Button
            variant="deletePrimary"
            onClick={async () => {
              await visibleFieldsMutate({
                userRole,
                namespace,
                visibleFields: visibleFieldData?.default_fields ?? []
              }).catch(noop)
              onClose()
            }}>
            <Msg id="settings.list_card_view_defaults.restore_default" />
          </Button>
          <Button onClick={onClose}>
            <Msg id="common.ui.cancel" />
          </Button>
        </ButtonGroupActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

function DisplayVisibleFields({
  title,
  isRestoreDefault = false,
  styleProps = {}
}: {
  title?: string
  isRestoreDefault?: boolean
  styleProps?: any
}) {
  const { visibleFields, visibleFieldData } = use(ListCardViewFieldSettingsContext)
  const fields = isRestoreDefault ? visibleFieldData?.default_fields : visibleFields
  const fieldsWithOptions = fields
    ?.filter(visibleField => visibleField.options)
    ?.sort((a, b) => a.display_order - b.display_order)

  return (
    <Fieldset {...styleProps}>
      <Legend>{title}</Legend>
      {fields
        ?.filter(visibleField => visibleField.value !== false && !visibleField.options)
        ?.sort((a, b) => a.display_order - b.display_order)
        ?.map(visibleField => (
          <Stack isInline spacing={4} my={2} key={`${visibleField.key}`}>
            {getGrayIcon(visibleField.icon)}
            <Text>{visibleField.label}</Text>
          </Stack>
        ))}
      {fieldsWithOptions && fieldsWithOptions.length > 0 && <Divider mt={3} />}
      {fieldsWithOptions?.map(visibleField => (
        <Stack isInline spacing={4} my={2} key={`${visibleField.key}`}>
          {getGrayIcon(listViewIcons.Generic)}
          <Text>{visibleField.options?.find(option => option.value === visibleField.value)?.label}</Text>
        </Stack>
      ))}
    </Fieldset>
  )
}

export function useCanUpdateConfig() {
  const { data: response } = useVisibleFieldsSettingsConfig()
  return response?.can_update_visible_fields_config ?? false
}
